import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Build as BuildIcon,
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { MaintenanceRecord, MaintenanceStatus, PaymentStatus } from '../../utils/types';
import { statusIcons } from '../../utils/constants';
import { getMaintenanceByDeviceId } from '../../services/maintenanceService';
import { currencyFormat } from '../../utils/constants';

interface MaintenanceReceiptDialogProps {
  open: boolean;
  onClose: () => void;
  deviceId: string;
  deviceInfo?: {
    type: string;
    brand?: string;
    model?: string;
    serialNumber?: string;
  };
}

// Status colors for maintenance
const statusColors: Record<MaintenanceStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  [MaintenanceStatus.PENDING]: 'warning',
  [MaintenanceStatus.IN_PROGRESS]: 'info',
  [MaintenanceStatus.COMPLETED]: 'success',
  [MaintenanceStatus.DELIVERED]: 'success',
  [MaintenanceStatus.CANCELLED]: 'error',
  [MaintenanceStatus.REFUNDED]: 'error',
  [MaintenanceStatus.RETURNED]: 'warning'
};

// Status labels in Arabic
const statusLabels: Record<MaintenanceStatus, string> = {
  [MaintenanceStatus.PENDING]: 'قيد الانتظار',
  [MaintenanceStatus.IN_PROGRESS]: 'قيد الصيانة',
  [MaintenanceStatus.COMPLETED]: 'تم الإصلاح',
  [MaintenanceStatus.DELIVERED]: 'تم التسليم',
  [MaintenanceStatus.CANCELLED]: 'مرتجع',
  [MaintenanceStatus.REFUNDED]: 'مرتجع مع استرداد',
  [MaintenanceStatus.RETURNED]: 'مرتجع مع رفض الجهاز'
};

// Status tooltips in Arabic
const statusTooltips: Record<MaintenanceStatus, string> = {
  [MaintenanceStatus.PENDING]: 'الطلب في انتظار بدء الصيانة',
  [MaintenanceStatus.IN_PROGRESS]: 'جاري العمل على إصلاح الجهاز',
  [MaintenanceStatus.COMPLETED]: 'تم إصلاح الجهاز وهو جاهز للتسليم',
  [MaintenanceStatus.DELIVERED]: 'تم تسليم الجهاز للعميل',
  [MaintenanceStatus.CANCELLED]: 'في انتظار الصيانة - تم إرجاع الجهاز قبل البدء',
  [MaintenanceStatus.REFUNDED]: 'في فترة ضمان الصيانة - تم إرجاع الجهاز مع استرداد المبلغ',
  [MaintenanceStatus.RETURNED]: 'تعدى مدة الضمان - تم إرجاع الجهاز مع رفض الإصلاح'
};

// Payment status colors
const paymentStatusColors: Record<PaymentStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  'unpaid': 'error',
  'partiallyPaid': 'warning',
  'partial': 'warning',
  'fullyPaid': 'success',
  'paid': 'success'
};

// Payment status labels in Arabic
const paymentStatusLabels: Record<PaymentStatus, string> = {
  'unpaid': 'غير مدفوع',
  'partiallyPaid': 'مدفوع جزئياً',
  'partial': 'مدفوع جزئياً',
  'fullyPaid': 'مدفوع بالكامل',
  'paid': 'مدفوع'
};

const MaintenanceReceiptDialog: React.FC<MaintenanceReceiptDialogProps> = ({
  open,
  onClose,
  deviceId,
  deviceInfo
}) => {
  const [maintenanceRecords, setMaintenanceRecords] = useState<MaintenanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && deviceId) {
      loadMaintenanceRecords();
    }
  }, [open, deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMaintenanceRecords = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading maintenance records for device:', deviceId);
      console.log('Device info:', deviceInfo);

      const records = await getMaintenanceByDeviceId(deviceId);
      console.log('Loaded maintenance records:', records);

      setMaintenanceRecords(records);

      if (records.length === 0) {
        setError(`لا توجد سجلات صيانة لهذا الجهاز (${deviceInfo?.type || 'غير محدد'})`);
        console.log('No maintenance records found for device ID:', deviceId);
        console.log('Device details:', {
          type: deviceInfo?.type,
          brand: deviceInfo?.brand,
          model: deviceInfo?.model,
          serialNumber: deviceInfo?.serialNumber
        });
      }
    } catch (err) {
      console.error('Error loading maintenance records:', err);
      setError('حدث خطأ أثناء تحميل سجلات الصيانة');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleViewMaintenanceDetails = (maintenanceId: string) => {
    const url = `/admin/maintenance/${maintenanceId}`;
    window.open(url, '_blank');
  };

  const formatDate = (date: Date | string | undefined): string => {
    if (!date) return 'غير محدد';
    
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'تاريخ غير صالح';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <ReceiptIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              إيصالات الصيانة - {deviceInfo?.type || 'جهاز غير محدد'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* معلومات الجهاز */}
        {deviceInfo && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom color="primary">
              معلومات الجهاز
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">معرف الجهاز</Typography>
                <Typography variant="body1" fontWeight="medium" sx={{ fontSize: '0.875rem', fontFamily: 'monospace' }}>
                  {deviceId}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">النوع</Typography>
                <Typography variant="body1" fontWeight="medium">{deviceInfo.type}</Typography>
              </Grid>
              {deviceInfo.brand && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">الماركة</Typography>
                  <Typography variant="body1" fontWeight="medium">{deviceInfo.brand}</Typography>
                </Grid>
              )}
              {deviceInfo.model && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">الموديل</Typography>
                  <Typography variant="body1" fontWeight="medium">{deviceInfo.model}</Typography>
                </Grid>
              )}
              {deviceInfo.serialNumber && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">الرقم التسلسلي</Typography>
                  <Typography variant="body1" fontWeight="medium">{deviceInfo.serialNumber}</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* محتوى الحوار */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              جاري تحميل سجلات الصيانة...
            </Typography>
          </Box>
        ) : error ? (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              {error}
            </Alert>

            {/* معلومات تشخيصية */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.100' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                معلومات التشخيص
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>معرف الجهاز:</strong> {deviceId}
              </Typography>
              {deviceInfo && (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>نوع الجهاز:</strong> {deviceInfo.type}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>الماركة:</strong> {deviceInfo.brand || 'غير محدد'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>الموديل:</strong> {deviceInfo.model || 'غير محدد'}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>الرقم التسلسلي:</strong> {deviceInfo.serialNumber || 'غير محدد'}
                  </Typography>
                </>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                تحقق من Console (F12) لمزيد من التفاصيل التشخيصية
              </Typography>
            </Paper>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                يمكنك إنشاء طلب صيانة جديد لهذا الجهاز من صفحة الصيانة
              </Typography>
              <Button
                variant="outlined"
                startIcon={<BuildIcon />}
                onClick={() => {
                  window.open('/admin/maintenance/create', '_blank');
                }}
              >
                إنشاء طلب صيانة جديد
              </Button>
            </Box>
          </Box>
        ) : maintenanceRecords.length > 0 ? (
          <Box>
            {maintenanceRecords.map((record, index) => (
              <Paper key={record.id} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                {/* رأس الإيصال */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" color="primary">
                    إيصال صيانة رقم: {record.orderNumber}
                  </Typography>
                  <Box display="flex" gap={1} alignItems="center">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      onClick={() => handleViewMaintenanceDetails(record.id)}
                      sx={{ mr: 1 }}
                    >
                      عرض التفاصيل الكاملة
                    </Button>
                    <Tooltip title={statusTooltips[record.status] || statusLabels[record.status]} placement="top">
                      <Chip
                        icon={statusIcons[record.status]}
                        label={undefined}
                        color={statusColors[record.status]}
                        size="small"
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          justifyContent: 'center',
                          alignItems: 'center',
                          '& .MuiChip-icon': {
                            margin: 0,
                            fontSize: '1rem',
                            marginLeft: 0,
                            marginRight: 0
                          }
                        }}
                      />
                    </Tooltip>
                    <Chip
                      label={paymentStatusLabels[record.paymentStatus]}
                      color={paymentStatusColors[record.paymentStatus]}
                      size="small"
                    />
                  </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* تفاصيل الإيصال */}
                <Grid container spacing={3}>
                  {/* معلومات أساسية */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      معلومات الطلب
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>معرف الطلب:</strong>
                        <Typography
                          component="span"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            ml: 0.5,
                            color: 'text.secondary'
                          }}
                        >
                          {record.id}
                        </Typography>
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>العميل:</strong> {record.customerName}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>تاريخ الاستلام:</strong> {formatDate(record.receivedAt)}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>المشكلة:</strong> {record.issue}
                      </Typography>
                      {record.diagnosis && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>التشخيص:</strong> {record.diagnosis}
                        </Typography>
                      )}
                      {record.branchName && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>الفرع:</strong> {record.branchName}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {/* معلومات مالية */}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      المعلومات المالية
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>التكلفة الأولية:</strong> {currencyFormat(record.initialCost)}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>التكلفة النهائية:</strong> {currencyFormat(record.finalCost)}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>المدفوع:</strong> {currencyFormat(record.paid)}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 0.5,
                          color: (record.finalCost || record.initialCost || 0) - record.paid > 0 ? 'error.main' : 'success.main',
                          fontWeight: 'medium'
                        }}
                      >
                        <strong>المتبقي:</strong> {currencyFormat((record.finalCost || record.initialCost || 0) - record.paid)}
                      </Typography>
                      {record.technicianShare > 0 && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>نصيب الفني:</strong> {currencyFormat(record.technicianShare)}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                {/* قطع الغيار المستخدمة */}
                {record.usedParts && record.usedParts.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      قطع الغيار المستخدمة
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>اسم القطعة</TableCell>
                            <TableCell align="center">الكمية</TableCell>
                            <TableCell align="right">السعر</TableCell>
                            <TableCell align="right">الإجمالي</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {record.usedParts.map((part, partIndex) => (
                            <TableRow key={partIndex}>
                              <TableCell>{part.name}</TableCell>
                              <TableCell align="center">{part.quantity}</TableCell>
                              <TableCell align="right">{currencyFormat(part.sellingPrice)}</TableCell>
                              <TableCell align="right">
                                {currencyFormat(part.quantity * part.sellingPrice)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* ملاحظات */}
                {record.notes && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      ملاحظات
                    </Typography>
                    <Typography variant="body2" sx={{ pl: 1, fontStyle: 'italic' }}>
                      {record.notes}
                    </Typography>
                  </Box>
                )}

                {/* معلومات إضافية */}
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        تاريخ الاستلام: {formatDate(record.receivedAt)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                        <Button
                          variant="text"
                          size="small"
                          startIcon={<OpenInNewIcon />}
                          onClick={() => handleViewMaintenanceDetails(record.id)}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          عرض في صفحة منفصلة
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <Alert severity="info">
            لا توجد سجلات صيانة لهذا الجهاز
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          إغلاق
        </Button>
        {maintenanceRecords.length > 0 && (
          <>
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon />}
              onClick={() => window.open('/admin/maintenance', '_blank')}
            >
              عرض جميع طلبات الصيانة
            </Button>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
            >
              طباعة
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceReceiptDialog;
